import {
  Alert,
  Button,
  Card,
  Table,
  Tag,
  Typography,
  Empty,
  Spin,
  Tooltip,
  Switch,
  Badge,
  Row,
  Col,
  Statistic,
  Input,
} from 'antd';
import {
  CalendarOutlined,
  HomeOutlined,
  EnvironmentOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  FilterOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { getColorForStatus, SOLICITUD_TIPOS } from 'assets/data/SolicitudesEstados';
import dayjs from 'libs/dayjs';
import { useSolicitudesMantencion } from 'providers/SolicitudesMantencionProvider';
import { useOnlineStatus } from 'hooks/useOnlineStatus';
import React, { useEffect, useRef, useState, useMemo } from 'react';
import SuccessButton from 'components/buttons/SuccessButton';
import ConfirmarInicioMantencionDrawer from 'components/mantenciones/ConfirmarInicioMantencionDrawer';
import CierreOfflineBadge from 'components/mantenciones/CierreOfflineBadge';
import { flushPendingCierres } from 'services/offlineCierreSync';
import { matchesSolicitudNumero } from 'utils/agendaSearch';

const AgendaSolicitudes = () => {
  const isOnline = useOnlineStatus();
  const {
    data,
    call,
    hasCalled,
    isLoading,
    startMantSolc,
    isLoadingStartMan,
    openDowloadMantencion,
    servedFromCache,
  } = useSolicitudesMantencion();

  const [showCompleted, setShowCompleted] = useState(false);
  const [confirmSolicitud, setConfirmSolicitud] = useState(null);
  const [search, setSearch] = useState('');

  const accionRequiereRed = (status) => status !== SOLICITUD_TIPOS.EN_CURSO;

  useEffect(() => {
    if (!hasCalled) {
      call();
    }
  }, [call, hasCalled]);

  // Al reconectar (offline -> online) refresca la lista: limpia el banner
  // de "datos guardados" y trae solicitudes nuevas que el daemon no propaga
  // automáticamente al estado del hook.
  const wasOnlineRef = useRef(isOnline);
  useEffect(() => {
    if (!wasOnlineRef.current && isOnline) {
      call();
    }
    wasOnlineRef.current = isOnline;
  }, [isOnline, call]);

  // Filtrar y ordenar los datos
  const filteredData = useMemo(() => {
    if (!data) return [];

    let filtered = [...data];

    // Eliminar duplicados usando id (id_solicitud) como identificador único
    filtered = filtered.filter(
      (item, index, self) => index === self.findIndex((t) => t.id === item.id)
    );

    // Por defecto solo mostrar las no finalizadas/enviadas
    if (!showCompleted) {
      filtered = filtered.filter(
        (item) =>
          item.status &&
          !item.status.toLowerCase().includes('finalizada') &&
          !item.status.toLowerCase().includes('enviada') &&
          !item.status.toLowerCase().includes('completada') &&
          !item.status.toLowerCase().includes('terminada')
      );
    }

    // Buscador por N° de solicitud o N° de mantención (vacío = sin filtro).
    filtered = filtered.filter((item) => matchesSolicitudNumero(item, search));

    // Orden por defecto: fecha de asignación descendente (más reciente primero).
    // En desktop la columna Fecha permite alternar asc/desc; el mobile usa este orden.
    return filtered.sort((a, b) => {
      const dateA = a.fecha_asignacion || a.date;
      const dateB = b.fecha_asignacion || b.date;
      return dayjs(dateB).diff(dayjs(dateA));
    });
  }, [data, showCompleted, search]);

  // Estadísticas directas de data
  const stats = useMemo(() => {
    if (!data) return { total: 0, active: 0, completed: 0 };

    const total = data.length;
    const completed = data.filter(
      (item) =>
        item.status &&
        (item.status.toLowerCase().includes('finalizada') ||
          item.status.toLowerCase().includes('enviada') ||
          item.status.toLowerCase().includes('completada') ||
          item.status.toLowerCase().includes('terminada'))
    ).length;
    const active = total - completed;

    return { total, active, completed };
  }, [data]);

  const startMan = async (item) => {
    // ASIGNADA → abrir drawer de confirmación (no crear directo).
    if (item?.status === SOLICITUD_TIPOS.ASIGNADA) {
      setConfirmSolicitud(item);
      return;
    }
    // Resto de estados (EN_CURSO navega, ENVIADA/FINALIZADA descargan): flujo actual.
    await startMantSolc(item);
    call();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {servedFromCache && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Mostrando datos guardados (sin conexión)"
          />
        )}
        {/* Header con estilo consistente */}
        <Card className="shadow-sm border-0">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div>
              <Typography.Title level={3} className="flex items-center space-x-2 mb-2">
                <CalendarOutlined className="text-blue-500" />
                <span>Solicitudes de Mantención</span>
              </Typography.Title>
              <Typography.Text type="secondary">
                Agenda de mantenciones programadas para técnicos
              </Typography.Text>
            </div>

            <div className="flex items-center space-x-2">
              <Input
                allowClear
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                prefix={<SearchOutlined className="text-gray-400" />}
                placeholder="Buscar por N° de solicitud o mantención"
                inputMode="numeric"
                style={{ width: 280, maxWidth: '100%' }}
              />
              <Button
                icon={<ReloadOutlined />}
                onClick={call}
                loading={isLoading}
                className="flex items-center space-x-2"
              >
                Actualizar
              </Button>
            </div>
          </div>
        </Card>

        {/* Estadísticas rápidas */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Card className="text-center hover:shadow-md transition-shadow">
              <Statistic
                title="Total"
                value={stats.total}
                prefix={<ClockCircleOutlined className="text-blue-500" />}
                valueStyle={{ color: '#1890ff', fontSize: '1.5rem' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card className="text-center hover:shadow-md transition-shadow">
              <Statistic
                title="Activas"
                value={stats.active}
                prefix={<ExclamationCircleOutlined className="text-orange-500" />}
                valueStyle={{ color: '#fa8c16', fontSize: '1.5rem' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card className="text-center hover:shadow-md transition-shadow">
              <Statistic
                title="Completadas"
                value={stats.completed}
                prefix={<CheckCircleOutlined className="text-green-500" />}
                valueStyle={{ color: '#52c41a', fontSize: '1.5rem' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Filtros para móvil */}
        <Card className="shadow-sm border-0 md:hidden">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FilterOutlined className="text-blue-500" />
              <Typography.Text strong>Filtros</Typography.Text>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={showCompleted}
                onChange={setShowCompleted}
                checkedChildren="Todas"
                unCheckedChildren="Activas"
                size="small"
              />
              <Badge count={filteredData.length} showZero>
                <Typography.Text type="secondary" className="text-sm">
                  {showCompleted ? 'Todas las mantenciones' : 'Solo activas'}
                </Typography.Text>
              </Badge>
            </div>
          </div>
        </Card>

        {/* Filtros para desktop */}
        <Card className="shadow-sm border-0 hidden md:block">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FilterOutlined className="text-blue-500" />
              <Typography.Text strong>Filtros</Typography.Text>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={showCompleted}
                onChange={setShowCompleted}
                checkedChildren="Todas"
                unCheckedChildren="Activas"
              />
              <Badge count={filteredData.length} showZero>
                <Typography.Text type="secondary">
                  {showCompleted ? 'Todas las mantenciones' : 'Solo activas'}
                </Typography.Text>
              </Badge>
            </div>
          </div>
        </Card>

        {/* Tabla optimizada para desktop */}
        <Card className="shadow-sm border-0 hidden md:block">
          <Typography.Title level={4} className="mb-4 flex items-center">
            <ClockCircleOutlined className="mr-2 text-blue-600" />
            {showCompleted ? 'Todas las Mantenciones' : 'Mantenciones Activas'}
          </Typography.Title>

          <Table
            dataSource={filteredData}
            loading={isLoading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} mantenciones`,
              pageSizeOptions: ['5', '10', '20', '50'],
            }}
            scroll={{ x: 800 }}
            size="middle"
            className="modern-table"
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No hay mantenciones programadas"
                />
              ),
            }}
            columns={[
              {
                title: (
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="font-medium">N° Solicitud</span>
                  </div>
                ),
                dataIndex: 'id',
                key: 'id',
                width: 120,
                render: (id, record) => (
                  <div>
                    <Typography.Text strong className="text-blue-600 font-semibold">
                      #{id}
                    </Typography.Text>
                    {record.id_mantencion && (
                      <div className="text-xs text-gray-500">Mant: #{record.id_mantencion}</div>
                    )}
                    {record.id_mantencion && (
                      <div className="mt-1">
                        <CierreOfflineBadge
                          mantencionId={record.id_mantencion}
                          onRetry={() => flushPendingCierres()}
                        />
                      </div>
                    )}
                  </div>
                ),
                sorter: (a, b) => a.id - b.id,
              },
              {
                title: (
                  <div className="flex items-center space-x-2">
                    <CalendarOutlined className="text-blue-600" />
                    <span className="font-medium">Fecha</span>
                  </div>
                ),
                dataIndex: 'fecha_asignacion',
                key: 'fecha_asignacion',
                width: 140,
                render: (date) => (
                  <div className="text-center">
                    <Typography.Text className="text-sm font-medium">
                      {dayjs(date).format('DD/MM')}
                    </Typography.Text>
                    <div className="text-xs text-gray-500">{dayjs(date).format('YYYY')}</div>
                  </div>
                ),
                sorter: (a, b) =>
                  dayjs(a.fecha_asignacion || a.date).diff(dayjs(b.fecha_asignacion || b.date)),
                defaultSortOrder: 'descend',
                sortDirections: ['descend', 'ascend'],
              },
              {
                title: (
                  <div className="flex items-center space-x-2">
                    <HomeOutlined className="text-green-600" />
                    <span className="font-medium">Edificio</span>
                  </div>
                ),
                dataIndex: 'Cliente',
                key: 'Cliente',
                width: 180,
                render: (Cliente) => (
                  <Typography.Text
                    className="font-medium text-gray-700 capitalize"
                    ellipsis={{ tooltip: Cliente?.name }}
                  >
                    {Cliente?.name}
                  </Typography.Text>
                ),
              },
              {
                title: (
                  <div className="flex items-center space-x-2">
                    <EnvironmentOutlined className="text-orange-600" />
                    <span className="font-medium">Dirección</span>
                  </div>
                ),
                dataIndex: 'Cliente',
                key: 'address',
                width: 200,
                render: (Cliente) => (
                  <Typography.Text
                    className="text-sm"
                    ellipsis={{
                      tooltip: `${Cliente?.address}, ${Cliente?.commune}`,
                    }}
                  >
                    <span className="capitalize">{Cliente?.address}</span>
                    {Cliente?.commune && (
                      <div className="text-xs text-gray-500 capitalize">{Cliente?.commune}</div>
                    )}
                  </Typography.Text>
                ),
              },
              {
                title: (
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    <span className="font-medium">Estado</span>
                  </div>
                ),
                dataIndex: 'status',
                key: 'status',
                width: 130,
                render: (status) => (
                  <Tag
                    color={getColorForStatus(status)}
                    className="font-medium px-3 py-1 rounded-full border-0"
                  >
                    {status}
                  </Tag>
                ),
              },
              {
                title: (
                  <div className="flex items-center justify-center space-x-2">
                    <PlayCircleOutlined className="text-blue-600" />
                    <span className="font-medium">Acción</span>
                  </div>
                ),
                key: 'actions',
                width: 140,
                align: 'center',
                render: (_, record) => {
                  const bloqueado = !isOnline && accionRequiereRed(record.status);
                  return (
                    <Tooltip
                      title={
                        bloqueado
                          ? 'Se requiere conexión para iniciar la visita'
                          : `Iniciar ${record.status?.toLowerCase()}`
                      }
                    >
                      <Button
                        onClick={() => startMan(record)}
                        type="primary"
                        size="small"
                        loading={isLoadingStartMan}
                        icon={<PlayCircleOutlined />}
                        className="font-medium"
                        disabled={bloqueado}
                        block
                      >
                        {record.status?.toUpperCase()}
                      </Button>
                    </Tooltip>
                  );
                },
              },
            ]}
          />
        </Card>

        {/* Vista móvil con tarjetas */}
        <div className="md:hidden">
          <Typography.Title level={4} className="mb-4 flex items-center">
            <ClockCircleOutlined className="mr-2 text-blue-600" />
            {showCompleted ? 'Todas las Mantenciones' : 'Mantenciones Activas'}
          </Typography.Title>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spin size="large" />
            </div>
          ) : filteredData.length === 0 ? (
            <Card className="shadow-sm border-0">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  showCompleted ? 'No hay mantenciones registradas' : 'No hay mantenciones activas'
                }
              />
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredData.map((item) => (
                <Card
                  key={item.id}
                  className="shadow-sm border-0 hover:shadow-md transition-shadow"
                >
                  <div className="space-y-3">
                    {/* Header de la card */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        <div>
                          <Typography.Text strong className="text-blue-600">
                            #{item.id}
                          </Typography.Text>
                          {item.id_mantencion && (
                            <div className="text-xs text-gray-500">Mant: #{item.id_mantencion}</div>
                          )}
                        </div>
                      </div>
                      <Tag
                        color={getColorForStatus(item.status)}
                        className="font-medium px-3 py-1 rounded-full border-0"
                      >
                        {item.status}
                      </Tag>
                    </div>

                    {item.id_mantencion && (
                      <CierreOfflineBadge
                        mantencionId={item.id_mantencion}
                        onRetry={() => flushPendingCierres()}
                      />
                    )}

                    {/* Información del cliente */}
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <HomeOutlined className="text-green-600" />
                        <Typography.Text className="font-medium capitalize">
                          {item.Cliente?.name}
                        </Typography.Text>
                      </div>

                      <div className="flex items-center space-x-2">
                        <EnvironmentOutlined className="text-orange-600" />
                        <Typography.Text className="text-sm text-gray-600 capitalize">
                          {item.Cliente?.address}
                        </Typography.Text>
                      </div>

                      <div className="flex items-center space-x-2">
                        <CalendarOutlined className="text-blue-600" />
                        <Typography.Text className="text-sm">
                          {dayjs(item.fecha_asignacion).format('DD/MM/YYYY')}
                        </Typography.Text>
                      </div>
                    </div>

                    {/* Botón de acción */}
                    <div className="pt-2 border-t border-gray-100">
                      {item.status &&
                      (item.status.toLowerCase().includes('finalizada') ||
                        item.status.toLowerCase().includes('enviada') ||
                        item.status.toLowerCase().includes('completada') ||
                        item.status.toLowerCase().includes('terminada')) ? (
                        <div className="space-y-2">
                          <div className="flex items-center justify-center space-x-2 py-2">
                            <CheckCircleOutlined className="text-green-500" />
                            <Typography.Text className="text-green-600 font-medium">
                              Mantención Completada
                            </Typography.Text>
                          </div>
                          <SuccessButton
                            onClick={() => openDowloadMantencion(item)}
                            size="large"
                            icon={<DownloadOutlined />}
                            className="w-full font-medium"
                            block
                          >
                            Descargar Mantención
                          </SuccessButton>
                        </div>
                      ) : (
                        <Tooltip
                          title={
                            !isOnline && accionRequiereRed(item.status)
                              ? 'Se requiere conexión para iniciar la visita'
                              : ''
                          }
                        >
                          <span className="block w-full">
                            <Button
                              onClick={() => startMan(item)}
                              type="primary"
                              size="large"
                              loading={isLoadingStartMan}
                              icon={<PlayCircleOutlined />}
                              className="w-full font-medium"
                              disabled={!isOnline && accionRequiereRed(item.status)}
                              block
                            >
                              {item.status?.toLowerCase() === 'pendiente'
                                ? 'Iniciar Mantención'
                                : `Continuar ${item.status?.toUpperCase()}`}
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <ConfirmarInicioMantencionDrawer
        solicitud={confirmSolicitud}
        open={!!confirmSolicitud}
        onClose={() => setConfirmSolicitud(null)}
      />
    </div>
  );
};

export default AgendaSolicitudes;
